import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Loader2, CheckCircle2 } from "lucide-react";
import { useScan } from "@/contexts/ScanContext";

export function ScanStatusIndicator() {
  const { state } = useScan();
  const [showComplete, setShowComplete] = useState(false);

  const isActive = state.isConnected && !state.isComplete;
  const justCompleted = state.isComplete && state.result !== null;

  // Show "Scan complete" briefly then auto-dismiss
  useEffect(() => {
    if (justCompleted) {
      setShowComplete(true);
      const timer = setTimeout(() => setShowComplete(false), 5000);
      return () => clearTimeout(timer);
    }
    setShowComplete(false);
  }, [justCompleted]);

  if (!isActive && !showComplete) return null;

  // Determine label and progress
  const { currentPhase, scanStats, screenshotStats, classificationStats, websiteStats } = state;

  let label = "Scanning";
  let progress: number | null = null; // null = indeterminate

  if (showComplete) {
    return (
      <Link
        to="/dashboard"
        className="ml-4 flex items-center gap-2 rounded-md bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400 transition-opacity hover:opacity-80"
      >
        <CheckCircle2 className="h-3.5 w-3.5" />
        <span>Scan complete</span>
      </Link>
    );
  }

  switch (currentPhase) {
    case "scanning": {
      const found = scanStats?.locationsFound ?? 0;
      label = `Scanning · ${found} found`;
      progress = null; // indeterminate
      break;
    }
    case "screenshotting": {
      const current = screenshotStats?.current ?? 0;
      const total = screenshotStats?.total ?? 0;
      label = `Screenshots · ${current}/${total}`;
      progress = total > 0 ? current / total : 0;
      break;
    }
    case "classifying": {
      const current = classificationStats?.current ?? 0;
      const total = classificationStats?.total ?? 0;
      label = `Classifying · ${current}/${total}`;
      progress = total > 0 ? current / total : 0;
      break;
    }
    case "website_classifying": {
      const current = websiteStats?.current ?? 0;
      const total = websiteStats?.total ?? 0;
      label = `Websites · ${current}/${total}`;
      progress = total > 0 ? current / total : 0;
      break;
    }
    default: {
      label = "Starting scan";
      progress = null;
    }
  }

  return (
    <Link
      to="/dashboard"
      className="ml-4 flex items-center gap-2 rounded-md bg-accent/10 px-3 py-1.5 transition-colors hover:bg-accent/15"
    >
      <Loader2 className="h-3.5 w-3.5 animate-spin text-accent" />
      <div className="flex flex-col gap-0.5">
        <span className="text-xs font-medium text-foreground leading-none">{label}</span>
        <div className="h-1 w-24 overflow-hidden rounded-full bg-accent/20">
          {progress === null ? (
            <div
              className="h-full w-1/2 rounded-full bg-accent"
              style={{ animation: "scan-progress-pulse 1.5s ease-in-out infinite" }}
            />
          ) : (
            <div
              className="h-full rounded-full bg-accent transition-[width] duration-300"
              style={{ width: `${Math.min(progress * 100, 100)}%` }}
            />
          )}
        </div>
      </div>
    </Link>
  );
}
